import { useState, useEffect, useCallback } from 'react';
import { useNavigate } from 'react-router-dom';
import { motion, AnimatePresence } from 'framer-motion';
import { Button } from '@/components/ui/button';
import {
  ChevronLeft, ChevronRight, GraduationCap, ShieldCheck, Users, Sparkles,
  BookOpen, Heart, X, Coins, Flame, Trophy, Crown, Star, Sun, School,
  ArrowRight, FileText
} from 'lucide-react';

/* ----------------------------- SLIDE CONTENT ----------------------------- */

const SLIDES = [
  {
    id: 'cover',
    bg: 'from-amber-100 via-rose-50 to-sky-100',
    accent: 'amber',
  },
  {
    id: 'what-is-susu',
    bg: 'from-rose-50 via-amber-50 to-orange-50',
    accent: 'rose',
  },
  {
    id: 'mechanics',
    bg: 'from-sky-50 via-emerald-50 to-amber-50',
    accent: 'emerald',
  },
  {
    id: 'compliance',
    bg: 'from-emerald-50 via-sky-50 to-indigo-50',
    accent: 'emerald',
  },
  {
    id: 'classroom-flow',
    bg: 'from-indigo-50 via-violet-50 to-rose-50',
    accent: 'indigo',
  },
  {
    id: 'outcomes',
    bg: 'from-violet-50 via-rose-50 to-amber-50',
    accent: 'violet',
  },
  {
    id: 'why-schools-approve',
    bg: 'from-amber-50 via-emerald-50 to-sky-50',
    accent: 'amber',
  },
  {
    id: 'gamification',
    bg: 'from-rose-100 via-amber-100 to-sky-100',
    accent: 'rose',
  },
  {
    id: 'pilot-plan',
    bg: 'from-sky-100 via-indigo-50 to-violet-50',
    accent: 'sky',
  },
  {
    id: 'cta',
    bg: 'from-amber-100 via-rose-100 to-amber-200',
    accent: 'amber',
  },
];

/* ----------------------------- PARALLAX SHAPES ----------------------------- */

const ParallaxShapes = ({ direction }) => (
  <>
    <motion.div
      key={`shape-1-${direction}`}
      initial={{ x: direction > 0 ? 200 : -200, opacity: 0 }}
      animate={{ x: 0, opacity: 0.3 }}
      transition={{ duration: 0.7, ease: [0.32, 0.72, 0, 1] }}
      className="absolute -top-20 -right-20 w-96 h-96 rounded-full bg-white/40 blur-3xl pointer-events-none"
    />
    <motion.div
      key={`shape-2-${direction}`}
      initial={{ x: direction > 0 ? 100 : -100, opacity: 0 }}
      animate={{ x: 0, opacity: 0.2 }}
      transition={{ duration: 0.9, ease: [0.32, 0.72, 0, 1] }}
      className="absolute -bottom-32 -left-32 w-[28rem] h-[28rem] rounded-full bg-white/40 blur-3xl pointer-events-none"
    />
  </>
);

/* ----------------------------- SLIDE COMPONENTS ----------------------------- */

const slideVariants = {
  enter: (direction) => ({
    x: direction > 0 ? '100%' : '-100%',
    opacity: 0,
  }),
  center: {
    x: 0,
    opacity: 1,
  },
  exit: (direction) => ({
    x: direction > 0 ? '-30%' : '30%',
    opacity: 0,
  }),
};

const SlideContainer = ({ children, direction, accentColor }) => (
  <motion.div
    custom={direction}
    variants={slideVariants}
    initial="enter"
    animate="center"
    exit="exit"
    transition={{
      x: { duration: 0.55, ease: [0.32, 0.72, 0, 1] },
      opacity: { duration: 0.35 },
    }}
    className="absolute inset-0 flex flex-col items-center justify-center px-6 sm:px-16 py-12"
  >
    <ParallaxShapes direction={direction} />
    <div className="relative z-10 max-w-5xl w-full">
      {children}
    </div>
  </motion.div>
);

const Reveal = ({ children, delay = 0, className = '' }) => (
  <motion.div
    initial={{ opacity: 0, y: 24 }}
    animate={{ opacity: 1, y: 0 }}
    transition={{ duration: 0.6, delay: 0.15 + delay, ease: [0.32, 0.72, 0, 1] }}
    className={className}
  >
    {children}
  </motion.div>
);

/* ----------------------------- INDIVIDUAL SLIDES ----------------------------- */

function CoverSlide({ direction }) {
  return (
    <SlideContainer direction={direction}>
      <Reveal>
        <div className="inline-flex items-center gap-2 bg-amber-200/60 text-amber-900 px-4 py-1.5 rounded-full text-sm font-semibold mb-6 backdrop-blur">
          <GraduationCap className="w-4 h-4" /> School Susu Program
        </div>
      </Reveal>
      <Reveal delay={0.08}>
        <h1 className="text-5xl sm:text-6xl lg:text-7xl font-bold tracking-tight text-stone-900 leading-tight">
          Teach saving
          <br />
          the way kids learn best:
          <br />
          <span className="text-amber-600">with their friends.</span>
        </h1>
      </Reveal>
      <Reveal delay={0.18}>
        <p className="mt-6 text-xl text-stone-700 max-w-2xl">
          A safe, parent-supervised classroom savings program where students contribute small amounts,
          cheer each other on, and watch each classmate take a turn winning the class pot.
        </p>
      </Reveal>
      <Reveal delay={0.28}>
        <div className="mt-10 grid grid-cols-2 sm:grid-cols-4 gap-3 max-w-3xl">
          {[
            { value: '$1', label: 'Min contribution' },
            { value: '0¢', label: 'Fees to schools' },
            { value: '100%', label: 'Parent-supervised' },
            { value: '8+', label: 'Recommended age' },
          ].map(s => (
            <div key={s.label} className="bg-white/70 backdrop-blur rounded-2xl p-4 text-center shadow-sm">
              <div className="text-3xl font-bold text-amber-600">{s.value}</div>
              <div className="text-xs text-stone-600 mt-1">{s.label}</div>
            </div>
          ))}
        </div>
      </Reveal>
    </SlideContainer>
  );
}

function WhatIsSusuSlide({ direction }) {
  return (
    <SlideContainer direction={direction}>
      <Reveal>
        <div className="inline-flex items-center gap-2 text-rose-700 font-semibold mb-3">
          <BookOpen className="w-5 h-5" /> Slide 02
        </div>
      </Reveal>
      <Reveal delay={0.06}>
        <h2 className="text-4xl sm:text-5xl font-bold text-stone-900 mb-6">What is School Susu?</h2>
      </Reveal>
      <div className="grid sm:grid-cols-2 gap-6 mt-4">
        <Reveal delay={0.14}>
          <div className="bg-white/70 backdrop-blur rounded-2xl p-6 h-full">
            <div className="text-rose-600 font-bold text-sm uppercase tracking-wider mb-2">The tradition</div>
            <p className="text-lg text-stone-800 leading-relaxed">
              <strong>Susu</strong> (also called <em>tanda, esusu, ROSCA</em>) is a centuries-old community
              savings tradition where a group contributes equally to a shared pot, and each member takes a
              turn receiving the full pot.
            </p>
          </div>
        </Reveal>
        <Reveal delay={0.22}>
          <div className="bg-white/70 backdrop-blur rounded-2xl p-6 h-full">
            <div className="text-amber-600 font-bold text-sm uppercase tracking-wider mb-2">For the classroom</div>
            <p className="text-lg text-stone-800 leading-relaxed">
              School Susu adapts this proven model into a <strong>classroom-friendly, gamified
              financial-literacy program</strong> for students aged 8 and up.
            </p>
          </div>
        </Reveal>
      </div>
      <Reveal delay={0.32}>
        <div className="mt-6 bg-amber-100/70 backdrop-blur rounded-2xl p-5 border border-amber-200">
          <p className="text-stone-800 leading-relaxed text-base">
            Each student contributes a small amount on a chosen cadence. When everyone has contributed
            for the round, the day's pot is awarded to the next student in the rotation.{' '}
            <strong>By the time the program completes, every student has had a turn.</strong>
          </p>
        </div>
      </Reveal>
    </SlideContainer>
  );
}

function MechanicsSlide({ direction }) {
  const steps = [
    { icon: Users, label: 'Parent funds Mini Bank', desc: 'Real $ stays at parent layer' },
    { icon: Coins, label: 'Kid earns coins', desc: 'Virtual currency only' },
    { icon: Trophy, label: 'Class hits 100%', desc: 'Pot auto-pays the recipient' },
    { icon: Heart, label: 'Recipient parent receives $', desc: 'Real money. Real impact.' },
  ];
  return (
    <SlideContainer direction={direction}>
      <Reveal>
        <div className="inline-flex items-center gap-2 text-emerald-700 font-semibold mb-3">
          <Sparkles className="w-5 h-5" /> Slide 03
        </div>
      </Reveal>
      <Reveal delay={0.06}>
        <h2 className="text-4xl sm:text-5xl font-bold text-stone-900 mb-3">The mechanics</h2>
      </Reveal>
      <Reveal delay={0.12}>
        <p className="text-lg text-stone-700 mb-8 max-w-3xl">
          One simple flow. Compliance-safe by design — kids never touch real money.
        </p>
      </Reveal>
      <div className="grid sm:grid-cols-2 lg:grid-cols-4 gap-4">
        {steps.map((s, i) => (
          <Reveal key={s.label} delay={0.18 + i * 0.08}>
            <div className="bg-white/80 backdrop-blur rounded-2xl p-5 h-full border border-emerald-100 relative">
              <div className="absolute -top-3 -left-3 bg-emerald-500 text-white rounded-full w-8 h-8 flex items-center justify-center font-bold text-sm shadow-md">
                {i + 1}
              </div>
              <s.icon className="w-8 h-8 text-emerald-600 mb-3" />
              <div className="font-bold text-stone-900 text-lg mb-1">{s.label}</div>
              <div className="text-sm text-stone-600">{s.desc}</div>
            </div>
          </Reveal>
        ))}
      </div>
      <Reveal delay={0.55}>
        <div className="mt-8 inline-flex items-center gap-2 bg-emerald-100/70 backdrop-blur rounded-full px-5 py-2 text-emerald-900 font-medium border border-emerald-200">
          <ShieldCheck className="w-5 h-5" /> No bank account required from any student
        </div>
      </Reveal>
    </SlideContainer>
  );
}

function ComplianceSlide({ direction }) {
  const items = [
    { title: 'No real money is held by minors', desc: 'Kids only see virtual coins, badges, and streaks. All real-money flows occur at the parent\'s account layer.' },
    { title: 'Parents own every transaction', desc: 'Each kid is a sub-profile under a verified parent account. Nothing happens without parental authorization.' },
    { title: 'No bank account is required from the student', desc: 'Funds move from parent to parent via the platform\'s regulated payment partners.' },
    { title: 'COPPA / PIPEDA / GDPR-K aligned', desc: 'No under-13 data collection without verifiable parental consent. Region-aware compliance.' },
    { title: 'Schools collect no payment information', desc: 'Schools have no fiduciary role. Teachers act only as optional class coordinators.' },
  ];
  return (
    <SlideContainer direction={direction}>
      <Reveal>
        <div className="inline-flex items-center gap-2 text-emerald-700 font-semibold mb-3">
          <ShieldCheck className="w-5 h-5" /> Slide 04
        </div>
      </Reveal>
      <Reveal delay={0.06}>
        <h2 className="text-4xl sm:text-5xl font-bold text-stone-900 mb-2">Built for compliance</h2>
        <p className="text-xl text-stone-700">No legal grey areas. No school liability.</p>
      </Reveal>
      <div className="mt-8 grid sm:grid-cols-2 gap-4">
        {items.map((item, i) => (
          <Reveal key={item.title} delay={0.15 + i * 0.07}>
            <div className="bg-white/75 backdrop-blur rounded-xl p-5 border-l-4 border-emerald-500">
              <div className="font-bold text-stone-900 mb-1">{item.title}</div>
              <div className="text-sm text-stone-600">{item.desc}</div>
            </div>
          </Reveal>
        ))}
      </div>
    </SlideContainer>
  );
}

function ClassroomFlowSlide({ direction }) {
  const steps = [
    'Teacher (or parent volunteer) creates a class on the platform and shares a 6-character class code.',
    'Each parent joins the class on behalf of their child, picks an avatar, and confirms the contribution.',
    'On contribution day, parents tap a button to send the day\'s amount from their secure Mini Bank wallet.',
    'When 100% of the class participates, the day\'s pot auto-releases to the parent of the student up next.',
    'Every classmate takes a turn — winning a meaningful amount for school trips, sports, or a savings bond.',
  ];
  return (
    <SlideContainer direction={direction}>
      <Reveal>
        <div className="inline-flex items-center gap-2 text-indigo-700 font-semibold mb-3">
          <Users className="w-5 h-5" /> Slide 05
        </div>
      </Reveal>
      <Reveal delay={0.06}>
        <h2 className="text-4xl sm:text-5xl font-bold text-stone-900 mb-8">How a classroom uses it</h2>
      </Reveal>
      <div className="space-y-3">
        {steps.map((step, i) => (
          <Reveal key={i} delay={0.12 + i * 0.08}>
            <div className="flex items-start gap-4 bg-white/75 backdrop-blur rounded-xl p-4">
              <div className="flex-shrink-0 w-10 h-10 rounded-full bg-indigo-500 text-white flex items-center justify-center font-bold">
                {i + 1}
              </div>
              <p className="text-stone-800 text-lg leading-relaxed pt-1">{step}</p>
            </div>
          </Reveal>
        ))}
      </div>
    </SlideContainer>
  );
}

function OutcomesSlide({ direction }) {
  const outcomes = [
    { title: 'Delayed gratification', desc: 'Daily action; reward arrives later.' },
    { title: 'Compounding habits', desc: 'Streaks reinforce consistency over time.' },
    { title: 'Community contribution', desc: 'Kids see their participation helps a peer.' },
    { title: 'Math integration', desc: 'Pot size, fractions, percentages tie to lessons.' },
    { title: 'Empathy & cheering', desc: 'Class meter rewards everyone at 100%.' },
    { title: 'Family conversations', desc: 'Every contribution is a parent-child money chat.' },
  ];
  return (
    <SlideContainer direction={direction}>
      <Reveal>
        <div className="inline-flex items-center gap-2 text-violet-700 font-semibold mb-3">
          <Sparkles className="w-5 h-5" /> Slide 06
        </div>
      </Reveal>
      <Reveal delay={0.06}>
        <h2 className="text-4xl sm:text-5xl font-bold text-stone-900 mb-2">Educational outcomes</h2>
        <p className="text-xl text-stone-700">Aligned to curriculum & social-emotional learning.</p>
      </Reveal>
      <div className="mt-8 grid sm:grid-cols-2 lg:grid-cols-3 gap-4">
        {outcomes.map((o, i) => (
          <Reveal key={o.title} delay={0.14 + i * 0.06}>
            <div className="bg-white/80 backdrop-blur rounded-2xl p-5 h-full">
              <div className="text-violet-600 font-bold text-sm uppercase tracking-wider mb-1">
                Outcome {i + 1}
              </div>
              <div className="font-bold text-stone-900 text-lg mb-1">{o.title}</div>
              <div className="text-sm text-stone-600">{o.desc}</div>
            </div>
          </Reveal>
        ))}
      </div>
    </SlideContainer>
  );
}

function WhySchoolsApproveSlide({ direction }) {
  const reasons = [
    { stat: '0', label: 'School liability', desc: 'Money flows between parents on the platform.' },
    { stat: '$0', label: 'Cost to schools', desc: 'Zero fees beyond the chosen contribution.' },
    { stat: 'Optional', label: 'Teacher involvement', desc: 'Teachers can coordinate or stay uninvolved.' },
    { stat: '$0.25', label: 'Lowest contribution', desc: 'Inclusive by design — every family can join.' },
    { stat: '200', label: 'Days per rotation', desc: 'Aligns with the academic year.' },
    { stat: '1', label: 'Tap per day', desc: 'Anti-screen-addiction by design.' },
  ];
  return (
    <SlideContainer direction={direction}>
      <Reveal>
        <div className="inline-flex items-center gap-2 text-amber-700 font-semibold mb-3">
          <Heart className="w-5 h-5" /> Slide 07
        </div>
      </Reveal>
      <Reveal delay={0.06}>
        <h2 className="text-4xl sm:text-5xl font-bold text-stone-900 mb-8">Why a school should approve</h2>
      </Reveal>
      <div className="grid sm:grid-cols-2 lg:grid-cols-3 gap-4">
        {reasons.map((r, i) => (
          <Reveal key={r.label} delay={0.14 + i * 0.07}>
            <div className="bg-white/80 backdrop-blur rounded-2xl p-5">
              <div className="text-5xl font-bold text-amber-600 mb-1">{r.stat}</div>
              <div className="font-bold text-stone-900">{r.label}</div>
              <div className="text-sm text-stone-600 mt-1">{r.desc}</div>
            </div>
          </Reveal>
        ))}
      </div>
    </SlideContainer>
  );
}

function GamificationSlide({ direction }) {
  const items = [
    { Icon: Star, name: 'First Save', color: 'text-yellow-600' },
    { Icon: Flame, name: 'On Fire (7-day)', color: 'text-orange-600' },
    { Icon: Crown, name: 'Saving Champion (30-day)', color: 'text-amber-600' },
    { Icon: Trophy, name: 'Big Day', color: 'text-rose-600' },
    { Icon: Sparkles, name: 'Class Hero', color: 'text-violet-600' },
    { Icon: Users, name: 'Multi-Clubber', color: 'text-indigo-600' },
    { Icon: Sun, name: 'Early Bird', color: 'text-sky-600' },
    { Icon: Coins, name: 'Super Saver', color: 'text-emerald-600' },
  ];
  return (
    <SlideContainer direction={direction}>
      <Reveal>
        <div className="inline-flex items-center gap-2 text-rose-700 font-semibold mb-3">
          <Sparkles className="w-5 h-5" /> Slide 08
        </div>
      </Reveal>
      <Reveal delay={0.06}>
        <h2 className="text-4xl sm:text-5xl font-bold text-stone-900 mb-2">Gamification (no ads, ever)</h2>
        <p className="text-xl text-stone-700">Age-appropriate. Habit-forming, not addictive.</p>
      </Reveal>
      <Reveal delay={0.14}>
        <div className="mt-6 mb-4">
          <div className="text-sm font-semibold text-stone-700 mb-3">8 BADGES TO EARN</div>
          <div className="grid grid-cols-2 sm:grid-cols-4 gap-3">
            {items.map((b, i) => (
              <motion.div
                key={b.name}
                initial={{ opacity: 0, scale: 0.8 }}
                animate={{ opacity: 1, scale: 1 }}
                transition={{ duration: 0.4, delay: 0.2 + i * 0.05, ease: 'backOut' }}
                className="bg-white/80 backdrop-blur rounded-2xl p-4 text-center"
              >
                <b.Icon className={`w-10 h-10 mx-auto mb-2 ${b.color}`} />
                <div className="text-xs font-semibold text-stone-800">{b.name}</div>
              </motion.div>
            ))}
          </div>
        </div>
      </Reveal>
      <Reveal delay={0.55}>
        <div className="grid sm:grid-cols-3 gap-3 mt-2">
          {[
            ['Avatars', '10 friendly characters'],
            ['Streaks', 'Daily counter, encouraging'],
            ['Class Leaderboard', 'Ranks by streak, not amount'],
          ].map(([title, desc]) => (
            <div key={title} className="bg-rose-100/60 backdrop-blur rounded-xl p-3">
              <div className="font-bold text-rose-900">{title}</div>
              <div className="text-xs text-stone-700">{desc}</div>
            </div>
          ))}
        </div>
      </Reveal>
    </SlideContainer>
  );
}

function PilotPlanSlide({ direction }) {
  const phases = [
    { week: 'Week 1', label: 'Pilot starts', desc: 'One classroom. 20–30 students. $1 daily, 4-week rotation.' },
    { week: 'Week 2', label: 'Family night', desc: 'Parents and kids review streaks together at home.' },
    { week: 'Week 3', label: 'First Big Days', desc: 'Students see a peer "win" the class pot.' },
    { week: 'Week 4', label: 'Pilot review', desc: 'Survey data on family money conversations.' },
    { week: 'Term 2', label: 'Scale up', desc: 'Roll out to grade-level if pilot succeeds.' },
  ];
  return (
    <SlideContainer direction={direction}>
      <Reveal>
        <div className="inline-flex items-center gap-2 text-sky-700 font-semibold mb-3">
          <School className="w-5 h-5" /> Slide 09
        </div>
      </Reveal>
      <Reveal delay={0.06}>
        <h2 className="text-4xl sm:text-5xl font-bold text-stone-900 mb-8">Suggested pilot plan</h2>
      </Reveal>
      <div className="relative">
        <div className="absolute left-6 sm:left-12 top-0 bottom-0 w-0.5 bg-gradient-to-b from-sky-300 to-violet-300" />
        <div className="space-y-3">
          {phases.map((p, i) => (
            <Reveal key={p.week} delay={0.16 + i * 0.1}>
              <div className="flex items-start gap-4 sm:gap-6">
                <div className="flex-shrink-0 w-12 sm:w-24 text-right">
                  <div className="font-bold text-sky-700 text-sm sm:text-base">{p.week}</div>
                </div>
                <div className="flex-shrink-0 w-3 h-3 mt-2 rounded-full bg-sky-500 ring-4 ring-sky-100 z-10" />
                <div className="flex-1 bg-white/80 backdrop-blur rounded-xl p-4">
                  <div className="font-bold text-stone-900">{p.label}</div>
                  <div className="text-sm text-stone-600 mt-1">{p.desc}</div>
                </div>
              </div>
            </Reveal>
          ))}
        </div>
      </div>
    </SlideContainer>
  );
}

function CtaSlide({ direction, navigate }) {
  return (
    <SlideContainer direction={direction}>
      <Reveal>
        <div className="text-center">
          <motion.div
            initial={{ scale: 0.8, opacity: 0 }}
            animate={{ scale: 1, opacity: 1 }}
            transition={{ duration: 0.6, ease: 'backOut' }}
            className="inline-flex items-center gap-2 bg-amber-300/60 backdrop-blur text-amber-900 px-5 py-2 rounded-full text-base font-bold mb-6"
          >
            <Sparkles className="w-5 h-5" /> Final slide
          </motion.div>
          <h2 className="text-5xl sm:text-7xl font-bold text-stone-900 mb-6">
            Ready to pilot?
          </h2>
          <p className="text-xl sm:text-2xl text-stone-700 max-w-2xl mx-auto mb-10">
            We work with school administrators to ensure rollout is smooth, age-appropriate, and aligned with
            your district's financial literacy standards.
          </p>
          <div className="flex flex-col sm:flex-row gap-3 justify-center">
            <Button
              size="lg"
              onClick={() => navigate('/kids')}
              className="bg-amber-500 hover:bg-amber-600 text-lg px-6"
              data-testid="cta-kids-hub"
            >
              Open Kids Hub <ArrowRight className="w-5 h-5 ml-2" />
            </Button>
            <Button
              size="lg"
              variant="outline"
              onClick={() => navigate('/school-pitch')}
              className="text-lg px-6"
              data-testid="cta-vertical-version"
            >
              <FileText className="w-5 h-5 mr-2" /> Vertical / printable version
            </Button>
          </div>
          <p className="text-xs text-stone-500 mt-10">
            School Susu Program · Worldwide Susu Money · v1.0
          </p>
        </div>
      </Reveal>
    </SlideContainer>
  );
}

const SLIDE_RENDERERS = [
  CoverSlide, WhatIsSusuSlide, MechanicsSlide, ComplianceSlide,
  ClassroomFlowSlide, OutcomesSlide, WhySchoolsApproveSlide,
  GamificationSlide, PilotPlanSlide, CtaSlide,
];

/* ----------------------------- MAIN PAGE ----------------------------- */

export default function SchoolPitchSlides() {
  const navigate = useNavigate();
  const [index, setIndex] = useState(0);
  const [direction, setDirection] = useState(1);
  const total = SLIDES.length;

  const goNext = useCallback(() => {
    setDirection(1);
    setIndex(i => Math.min(i + 1, total - 1));
  }, [total]);

  const goPrev = useCallback(() => {
    setDirection(-1);
    setIndex(i => Math.max(i - 1, 0));
  }, []);

  const goTo = (i) => {
    setDirection(i > index ? 1 : -1);
    setIndex(i);
  };

  // Keyboard nav
  useEffect(() => {
    const onKey = (e) => {
      if (e.key === 'ArrowRight' || e.key === ' ' || e.key === 'PageDown') {
        e.preventDefault();
        goNext();
      } else if (e.key === 'ArrowLeft' || e.key === 'PageUp') {
        e.preventDefault();
        goPrev();
      } else if (e.key === 'Escape') {
        navigate('/school-pitch');
      } else if (e.key === 'Home') {
        setDirection(-1);
        setIndex(0);
      } else if (e.key === 'End') {
        setDirection(1);
        setIndex(total - 1);
      }
    };
    window.addEventListener('keydown', onKey);
    return () => window.removeEventListener('keydown', onKey);
  }, [goNext, goPrev, navigate, total]);

  const current = SLIDES[index];
  const Renderer = SLIDE_RENDERERS[index];

  return (
    <div
      className={`fixed inset-0 overflow-hidden bg-gradient-to-br ${current.bg} transition-colors duration-700`}
      data-testid="slides-container"
    >
      {/* Top bar */}
      <div className="absolute top-0 left-0 right-0 z-30 px-4 sm:px-6 py-4 flex justify-between items-center pointer-events-none">
        <div className="flex items-center gap-2 bg-white/60 backdrop-blur px-3 py-1.5 rounded-full shadow-sm pointer-events-auto">
          <GraduationCap className="w-4 h-4 text-amber-700" />
          <span className="text-sm font-semibold text-stone-800">School Susu Pitch</span>
        </div>
        <div className="flex items-center gap-3 pointer-events-auto">
          <span
            className="bg-white/60 backdrop-blur px-3 py-1.5 rounded-full text-sm font-medium text-stone-800 shadow-sm"
            data-testid="slide-counter"
          >
            {index + 1} / {total}
          </span>
          <Button
            size="sm"
            variant="outline"
            onClick={() => navigate('/school-pitch')}
            className="bg-white/70 backdrop-blur border-stone-300 hover:bg-white"
            data-testid="exit-slides-btn"
          >
            <X className="w-4 h-4 mr-1" /> Exit
          </Button>
        </div>
      </div>

      {/* Slide content */}
      <AnimatePresence custom={direction} mode="wait">
        <motion.div
          key={index}
          custom={direction}
          drag="x"
          dragConstraints={{ left: 0, right: 0 }}
          dragElastic={0.2}
          onDragEnd={(_, info) => {
            if (info.offset.x < -100 && index < total - 1) goNext();
            else if (info.offset.x > 100 && index > 0) goPrev();
          }}
          className="absolute inset-0"
          data-testid={`slide-${current.id}`}
        >
          <Renderer direction={direction} navigate={navigate} />
        </motion.div>
      </AnimatePresence>

      {/* Left arrow */}
      <motion.button
        whileHover={{ scale: 1.1, x: -3 }}
        whileTap={{ scale: 0.95 }}
        onClick={goPrev}
        disabled={index === 0}
        className={`absolute left-3 sm:left-6 top-1/2 -translate-y-1/2 z-30 w-14 h-14 sm:w-16 sm:h-16 rounded-full backdrop-blur shadow-lg flex items-center justify-center transition-opacity ${
          index === 0 ? 'opacity-30 cursor-not-allowed bg-white/50' : 'bg-white/80 hover:bg-white cursor-pointer'
        }`}
        aria-label="Previous slide"
        data-testid="prev-slide-btn"
      >
        <ChevronLeft className="w-7 h-7 text-stone-900" />
      </motion.button>

      {/* Right arrow */}
      <motion.button
        whileHover={{ scale: 1.1, x: 3 }}
        whileTap={{ scale: 0.95 }}
        onClick={goNext}
        disabled={index === total - 1}
        className={`absolute right-3 sm:right-6 top-1/2 -translate-y-1/2 z-30 w-14 h-14 sm:w-16 sm:h-16 rounded-full backdrop-blur shadow-lg flex items-center justify-center transition-opacity ${
          index === total - 1 ? 'opacity-30 cursor-not-allowed bg-white/50' : 'bg-amber-500 hover:bg-amber-600 cursor-pointer'
        }`}
        aria-label="Next slide"
        data-testid="next-slide-btn"
      >
        <ChevronRight className={`w-7 h-7 ${index === total - 1 ? 'text-stone-900' : 'text-white'}`} />
      </motion.button>

      {/* Dots navigation */}
      <div className="absolute bottom-6 left-0 right-0 z-30 flex justify-center items-center gap-2 px-4">
        {SLIDES.map((_, i) => (
          <button
            key={i}
            onClick={() => goTo(i)}
            className={`transition-all rounded-full ${
              i === index
                ? 'w-8 h-2 bg-amber-600'
                : 'w-2 h-2 bg-stone-400/60 hover:bg-stone-600'
            }`}
            aria-label={`Go to slide ${i + 1}`}
            data-testid={`dot-${i}`}
          />
        ))}
      </div>

      {/* Hint - only on first slide */}
      {index === 0 && (
        <motion.div
          initial={{ opacity: 0 }}
          animate={{ opacity: 1 }}
          transition={{ delay: 1.5, duration: 0.5 }}
          className="absolute bottom-16 left-0 right-0 text-center z-20 pointer-events-none"
        >
          <span className="inline-block bg-stone-900/70 text-white px-4 py-1.5 rounded-full text-xs">
            Click arrows · use ← → keys · or swipe
          </span>
        </motion.div>
      )}
    </div>
  );
}
